/**
 * XState State Machine for Conversation Management
 * 
 * This machine handles the conversation state between user and AI avatar.
 * It uses parallel states to manage:
 * - Connection lifecycle (idle, connecting, ready, error)
 * - Conversation flow (waiting, userSpeaking, waitingForTranscript, aiResponding)
 * 
 * REFACTORED (2026-01): Separates visual state from data state to fix disappearing bubbles.
 * 
 * Key insight: Messages are ONLY created when server confirms speech with item_id.
 * - VAD_SPEECH_DETECTED: Visual feedback only (no message created)
 * - SERVER_SPEECH_STARTED: Creates message with server's item_id
 * - TRANSCRIPT_RECEIVED: Direct lookup by item_id (no correlation needed)
 * 
 * This eliminates the speechId ↔ item_id correlation that was causing bubbles to disappear
 * when correlation failed due to race conditions or network delays.
 * 
 * Benefits:
 * - Impossible to enter invalid states
 * - All transitions are explicit and documented
 * - Visual debugging with XState DevTools
 * - Eliminates race conditions
 * - Messages always have valid server-assigned IDs
 */

import { setup, assign } from 'xstate';
import type { TranscriptMessage } from './types';
import type { AudioLevel } from '../../services/audio';

// ============================================================================
// Context Types
// ============================================================================

export interface ConversationContext {
  // Messages
  messages: TranscriptMessage[];
  currentTranscriptId: string | null; // Server's item_id for current speech
  transcriptInProgress: string;
  aiResponseInProgress: string;
  // 🎯 Text-audio sync: Full AI text waiting to be finalized (held while audio finishes playing)
  aiResponsePendingText: string | null;
  
  // Track active speech independent of conversation state
  // This is needed because user can speak while AI is responding (interruption)
  // and we need to show waveform even though we're in aiResponding state
  isUserActivelySpeaking: boolean;
  
  // Timestamps for message ordering
  userSpeechStoppedTimestamp?: number;
  aiResponseStartedTimestamp?: number;
  
  // Error handling
  error: Error | null;
  
  // Audio state (managed outside machine but tracked here)
  audioLevel: AudioLevel | null;
  isRecording: boolean;
  isPlaying: boolean;
  // 🎯 Text-audio sync: true once the first audio chunk for the current AI
  // response has arrived. Distinguishes "audio still expected" from "audio
  // received but not yet playing" (prebuffering) — the latter must still
  // delay finalization, otherwise AI_RESPONSE_DONE arriving during
  // prebuffering finalizes the text while audio hasn't even started playing.
  hasReceivedAudioForResponse: boolean;
  
  // Completion flags
  userTranscriptComplete: boolean;
  aiTextResponseComplete: boolean;
  
  // Mode settings
  inputMode: 'voice' | 'keyboard' | 'ptt';
  avatarAudioEnabled: boolean;
  microphoneFallback: boolean;
  
  // Analysis Mode
  analysisMode: boolean;
  analysisModeProcessing: boolean;
  
  // Retry tracking
  retryCount: number;
}

// ============================================================================
// Event Types
// ============================================================================

export type ConversationEvent =
  // Connection events
  | { type: 'CONNECT' }
  | { type: 'CONNECTED' }
  | { type: 'DISCONNECT' }
  | { type: 'CONNECTION_ERROR'; error: Error }
  | { type: 'RETRY' }
  
  // Speech events (REFACTORED: separated visual state from data state)
  // VAD_SPEECH_LIKELY: Early visual hint — waveform appears ~60ms into voiced audio, before gate opens
  | { type: 'VAD_SPEECH_LIKELY' }
  // VAD_SPEECH_DETECTED: Gate opened — audio streaming starts, recording begins
  | { type: 'VAD_SPEECH_DETECTED' }
  // SERVER_SPEECH_STARTED: Server confirmed speech with item_id - NOW create message
  | { type: 'SERVER_SPEECH_STARTED'; itemId: string }
  | { type: 'USER_SPEECH_STARTED' }
  | { type: 'SPEECH_STOPPED'; audioBlob?: Blob }
  | { type: 'NOISE_DETECTED' }
  
  // Transcript events (REFACTORED: itemId for direct lookup, no correlation needed)
  | { type: 'TRANSCRIPT_RECEIVED'; text: string; itemId: string; rawTranscript?: string }
  | { type: 'EMPTY_TRANSCRIPT'; itemId?: string }
  | { type: 'TRANSCRIPT_IN_PROGRESS'; text: string }
  
  // AI response events
  | { type: 'AI_RESPONSE_DELTA'; delta: string; fullText: string }
  | { type: 'AI_RESPONSE_DONE'; text: string }
  | { type: 'AI_AUDIO_DONE' }
  | { type: 'CANCEL_RESPONSE' }
  | { type: 'AI_FINISHING_TIMEOUT' } // 🎯 Text-audio sync: Safety timeout for aiFinishing state
  
  // Audio events
  | { type: 'RECORDING_STARTED' }
  | { type: 'RECORDING_STOPPED' }
  | { type: 'PLAYBACK_STARTED' }
  | { type: 'PLAYBACK_STOPPED' }
  | { type: 'AUDIO_LEVEL_CHANGED'; level: AudioLevel }
  
  // Mode events
  | { type: 'SET_INPUT_MODE'; mode: 'voice' | 'keyboard' | 'ptt'; enableMicrophone?: boolean }
  // Reset stuck user-speech state when switching input modes.
  // Dispatched before SET_INPUT_MODE to unstick the conversation state machine
  // if a previous mode left it trapped in userSpeaking/waitingForTranscript
  // (e.g. server VAD fired on degraded audio in keyboard mode and never sent
  // speech_stopped). Without this, PTT_PRESSED is dropped in userSpeaking state.
  | { type: 'RESET_USER_SPEECH' }
  
  // PTT events
  | { type: 'PTT_PRESSED'; pttId?: string }
  | { type: 'PTT_RELEASED' }
  | { type: 'PTT_SILENCE_DETECTED' }
  | { type: 'TOGGLE_AVATAR_AUDIO' }
  | { type: 'SET_MICROPHONE_FALLBACK'; enabled: boolean }
  
  // Analysis mode events
  | { type: 'ENTER_ANALYSIS_MODE' }
  | { type: 'EXIT_ANALYSIS_MODE' }
  | { type: 'SET_ANALYSIS_PROCESSING'; processing: boolean }
  
  // Error events
  | { type: 'CLEAR_ERROR' }
  
  // Message management
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'ADD_MESSAGE'; message: TranscriptMessage }
  | { type: 'UPDATE_MESSAGE'; id: string; updates: Partial<TranscriptMessage> }
  | { type: 'REMOVE_MESSAGE'; id: string };

// ============================================================================
// Initial Context
// ============================================================================

export const initialContext: ConversationContext = {
  messages: [],
  currentTranscriptId: null,
  transcriptInProgress: '',
  aiResponseInProgress: '',
  aiResponsePendingText: null,
  isUserActivelySpeaking: false,
  error: null,
  audioLevel: null,
  isRecording: false,
  isPlaying: false,
  hasReceivedAudioForResponse: false,
  userTranscriptComplete: false,
  aiTextResponseComplete: false,
  inputMode: 'ptt',
  avatarAudioEnabled: true,
  microphoneFallback: false,
  analysisMode: false,
  analysisModeProcessing: false,
  retryCount: 0,
};

// ============================================================================
// State Machine Definition
// ============================================================================

export const conversationMachine = setup({
  types: {
    context: {} as ConversationContext,
    events: {} as ConversationEvent,
  },
  
  actions: {
    // Connection actions
    resetRetryCount: assign({ retryCount: 0 }),
    incrementRetryCount: assign({ retryCount: ({ context }) => context.retryCount + 1 }),
    setError: assign({ error: (_, params: { error: Error }) => params.error }),
    clearError: assign({ error: null }),
    
    // Speech actions (REFACTORED: createPlaceholder now on SERVER_SPEECH_STARTED)
    // 
    // Old flow (broken): VAD -> create placeholder with local ID -> correlate with server ID
    // New flow (fixed): VAD -> visual only -> server speech_started -> create with server ID
    //
    // IMPORTANT: If we already have a pending placeholder (empty user message), don't create
    // another one. This handles the case where OpenAI's semantic_vad sends multiple
    // speech_started events during slow speech with pauses. Without this check, we'd create
    // multiple loading bubbles, and only the last one would receive the transcript.
    //
    createPlaceholder: assign(({ context, event }) => {
      if (event.type !== 'SERVER_SPEECH_STARTED') return {};
      
      const { itemId } = event;
      
      // Check if we already have a pending placeholder (empty user message)
      const existingPendingIndex = context.messages.findIndex(
        msg => msg.speaker === 'user' && !msg.text
      );
      
      if (existingPendingIndex !== -1) {
        // Update existing placeholder's ID to the latest itemId
        // This ensures the transcript (which comes with the latest itemId) will find it
        const updatedMessages = [...context.messages];
        updatedMessages[existingPendingIndex] = {
          ...updatedMessages[existingPendingIndex],
          id: itemId,
        };
        return {
          messages: updatedMessages,
          currentTranscriptId: itemId,
        };
      }
      
      const placeholderMessage: TranscriptMessage = {
        text: '',
        timestamp: Date.now(),
        speaker: 'user',
        id: itemId, // Use server's item_id directly - no correlation needed!
      };
      
      return {
        messages: [...context.messages, placeholderMessage],
        currentTranscriptId: itemId,
      };
    }),
    
    // PTT placeholder: Created on PTT_PRESSED with a local ID since
    // turn_detection is null and OpenAI won't send SERVER_SPEECH_STARTED.
    // The transcript will arrive later with the server's item_id; fillTranscript
    // will create a new message for it (edge case path).
    createPTTPlaceholder: assign(({ context, event }) => {
      // Don't create duplicate placeholders
      const existingPending = context.messages.find(
        msg => msg.speaker === 'user' && !msg.text
      );
      if (existingPending) return {};
      
      const pttId = (event.type === 'PTT_PRESSED' && event.pttId) ? event.pttId : `ptt-${Date.now()}`;
      const placeholderMessage: TranscriptMessage = {
        text: '',
        timestamp: Date.now(),
        speaker: 'user',
        id: pttId,
      };
      
      return {
        messages: [...context.messages, placeholderMessage],
        currentTranscriptId: pttId,
      };
    }),
    
    removePlaceholder: assign(({ context }) => {
      const { currentTranscriptId } = context;
      if (!currentTranscriptId) return {};
      
      return {
        messages: context.messages.filter(
          msg => !(msg.id === currentTranscriptId && msg.speaker === 'user' && !msg.text)
        ),
        currentTranscriptId: null,
        transcriptInProgress: '',
      };
    }),
    
    // Reset visual state when noise detected (no message to remove yet)
    resetVisualState: assign({
      transcriptInProgress: '',
      isUserActivelySpeaking: false,
    }),

    // Reset stuck user-speech state on mode switch.
    // Removes any pending placeholder (empty user message) and clears all
    // speech-related visual/data state. Used by RESET_USER_SPEECH event.
    resetUserSpeech: assign(({ context }) => {
      const cleanMessages = context.messages.filter(
        msg => !(msg.speaker === 'user' && !msg.text)
      );
      return {
        messages: cleanMessages,
        currentTranscriptId: null,
        transcriptInProgress: '',
        isUserActivelySpeaking: false,
        userSpeechStoppedTimestamp: undefined,
        userTranscriptComplete: false,
      };
    }),
    
    // Mark user as actively speaking (for waveform display during interruption)
    setUserActivelySpeaking: assign({
      isUserActivelySpeaking: true,
    }),
    
    // Clear active speaking flag and set timestamp
    setUserSpeechStopped: assign({
      userSpeechStoppedTimestamp: () => Date.now(),
      isUserActivelySpeaking: false,
    }),
    
    // Transcript actions
    updateTranscriptInProgress: assign(({ event }) => {
      if (event.type !== 'TRANSCRIPT_IN_PROGRESS') return {};
      return { transcriptInProgress: event.text };
    }),
    
    // REFACTORED: fillTranscript uses event.itemId directly for lookup
    // No more context.currentTranscriptId fallback needed - itemId is always present
    // Also clears isUserActivelySpeaking defensively to prevent ghost waveform bubbles
    fillTranscript: assign(({ context, event }) => {
      if (event.type !== 'TRANSCRIPT_RECEIVED') return {};
      
      const { text, rawTranscript, itemId } = event;
      
      // Direct lookup by itemId - no correlation needed!
      const messageExists = context.messages.some(msg => msg.id === itemId);
      
      if (!messageExists) {
        // No message found with server's itemId.
        // This commonly happens in PTT mode: the placeholder was created with a local
        // ptt-{timestamp} ID, but the transcript arrives with the server's item_id.
        // It can also happen in VAD mode if SERVER_SPEECH_STARTED was missed.
        //
        // FIRST: Check if there's a pending placeholder (empty user message) we can reuse.
        // This preserves the placeholder's original timestamp (correct chronological position)
        // and prevents "stuck loading bubble" artifacts.
        const pendingIdx = context.messages.findIndex(
          msg => msg.speaker === 'user' && !msg.text
        );
        
        if (pendingIdx !== -1) {
          // Reuse existing placeholder: update its ID to the server's item_id and fill text
          const updatedMessages = [...context.messages];
          updatedMessages[pendingIdx] = {
            ...updatedMessages[pendingIdx],
            id: itemId,
            text,
            rawTranscript,
          };
          return {
            messages: updatedMessages,
            transcriptInProgress: '',
            currentTranscriptId: null,
            userTranscriptComplete: true,
            userSpeechStoppedTimestamp: undefined,
            isUserActivelySpeaking: false,
          };
        }
        
        // No placeholder found either - create a brand new message.
        // IMPORTANT: Find correct timestamp to maintain conversation order.
        // If we use Date.now(), the user message would appear AFTER the AI response
        // (which was already finalized) because messages are sorted by timestamp.
        // We need to place the user message BEFORE the last AI message.
        const lastAiMessage = [...context.messages].reverse().find(m => m.speaker === 'ai');
        const timestamp = lastAiMessage 
          ? lastAiMessage.timestamp - 1  // Place just before last AI message
          : Date.now();
        
        const newMessage: TranscriptMessage = {
          text,
          timestamp,
          speaker: 'user',
          id: itemId,
          rawTranscript,
        };
        return {
          messages: [...context.messages, newMessage],
          transcriptInProgress: '',
          currentTranscriptId: null,
          userTranscriptComplete: true,
          userSpeechStoppedTimestamp: undefined,
          isUserActivelySpeaking: false,
        };
      }
      
      // Update existing message by itemId
      const updatedMessages = context.messages.map(msg =>
        msg.id === itemId
          ? { ...msg, text, rawTranscript }
          : msg
      );
      
      return {
        messages: updatedMessages,
        transcriptInProgress: '',
        currentTranscriptId: null,
        userTranscriptComplete: true,
        userSpeechStoppedTimestamp: undefined,
        isUserActivelySpeaking: false,
      };
    }),
    
    // REFACTORED: markUnintelligible uses event.itemId if available
    // Also clears isUserActivelySpeaking to prevent ghost waveform bubbles
    markUnintelligible: assign(({ context, event }) => {
      // Use itemId from event if available, otherwise fall back to context
      const targetId = (event.type === 'EMPTY_TRANSCRIPT' && event.itemId) 
        ? event.itemId 
        : context.currentTranscriptId;
      
      if (!targetId) return {
        transcriptInProgress: '',
        currentTranscriptId: null,
        userTranscriptComplete: true,
        userSpeechStoppedTimestamp: undefined,
        isUserActivelySpeaking: false,
      };
      
      // Remove placeholder for empty transcript (user didn't say anything meaningful)
      let cleanMessages = context.messages.filter(msg => msg.id !== targetId);
      
      // If no message was removed (ID mismatch, e.g. PTT mode where placeholder has ptt-*
      // but targetId is server's item_id), also try removing any pending empty user message
      if (cleanMessages.length === context.messages.length) {
        cleanMessages = context.messages.filter(
          msg => !(msg.speaker === 'user' && !msg.text)
        );
      }
      
      return {
        messages: cleanMessages,
        transcriptInProgress: '',
        currentTranscriptId: null,
        userTranscriptComplete: true,
        userSpeechStoppedTimestamp: undefined,
        isUserActivelySpeaking: false,
      };
    }),
    
    // Clear currentTranscriptId without modifying messages
    // Used when TRANSCRIPT_RECEIVED/EMPTY_TRANSCRIPT arrive in aiResponding state
    // where message updates are already handled via UPDATE_MESSAGE/REMOVE_MESSAGE
    // 
    // IMPORTANT: Also clears isUserActivelySpeaking because receiving a transcript
    // means speech has ended. Without this, the visual-only waveform bubble would
    // remain visible after transcript arrives (bug: waveform stays forever after interruption)
    clearTranscriptId: assign({
      currentTranscriptId: null,
      transcriptInProgress: '',
      userTranscriptComplete: true,
      userSpeechStoppedTimestamp: undefined,
      isUserActivelySpeaking: false,
    }),
    
    // AI response actions
    // 🔧 FIX: Use fullText instead of concatenating delta
    // XState batches events, so context.aiResponseInProgress + delta would use stale context
    // when multiple deltas arrive in the same batch, causing garbled/out-of-order text.
    // The fullText is accumulated synchronously in useTranscriptManager ref.
    appendAIResponseDelta: assign(({ context, event }) => {
      if (event.type !== 'AI_RESPONSE_DELTA') return {};
      
      const isFirstDelta = !context.aiResponseInProgress;
      
      return {
        aiResponseInProgress: event.fullText, // Use pre-accumulated fullText, not delta
        aiResponseStartedTimestamp: isFirstDelta ? Date.now() : context.aiResponseStartedTimestamp,
        aiTextResponseComplete: false,
      };
    }),
    
    // 🎯 Text-audio sync: Store AI text without finalizing — used when text completes
    // but audio is still playing. The text is held in aiResponsePendingText until
    // PLAYBACK_STOPPED fires, at which point finalizeAIResponse is called.
    storeAIResponsePending: assign(({ event }) => {
      if (event.type !== 'AI_RESPONSE_DONE') return {};
      return {
        aiResponsePendingText: event.text,
        aiTextResponseComplete: true, // Text is done, just waiting for audio
        // DON'T clear aiResponseInProgress — keep showing throttled text via useSyncedTextReveal
        // DON'T add to messages[] yet — wait for audio to finish
      };
    }),

    finalizeAIResponse: assign(({ context, event }) => {
      // 🎯 Text-audio sync: Use pending text if available (from aiFinishing state),
      // otherwise use event text (direct finalization when no audio)
      const text = context.aiResponsePendingText ?? (event.type === 'AI_RESPONSE_DONE' ? event.text : '');

      if (!text) return {};

      // 🔧 DEDUPLICATION: Prevent the same AI message from being added twice.
      // This can happen if both response.text.done and response.audio_transcript.done
      // fire for the same response (race condition with currentResponseHasAudio flag).
      const lastMessage = context.messages[context.messages.length - 1];
      if (lastMessage && lastMessage.speaker === 'ai' && lastMessage.text === text) {
        // Duplicate detected — skip adding, just clear the response state
        return {
          aiResponseInProgress: '',
          aiResponsePendingText: null,
          aiResponseStartedTimestamp: undefined,
          aiTextResponseComplete: true,
          userTranscriptComplete: false,
          currentTranscriptId: null,
          // 🎯 Text-audio sync: Reset audio-received flag for the next response
          hasReceivedAudioForResponse: false,
        };
      }

      // Use aiResponseStartedTimestamp so the AI message is placed chronologically
      // at the point the AI STARTED responding, not when it finished.
      //
      // Why: If the user interrupted during the AI response, their interrupt message
      // was created (via SERVER_SPEECH_STARTED) with a later timestamp. Using Date.now()
      // here would place the AI message AFTER the user's interrupt, giving the wrong
      // final order: User1, User2, AI. With aiResponseStartedTimestamp, the correct
      // order is preserved: User1, AI, User2.
      //
      // During streaming, TranscriptOverlay renders the user's interrupt bubble BELOW
      // the AIResponseBubble, so the visual transition to final order is smooth.
      const timestamp = context.aiResponseStartedTimestamp || Date.now();
      
      const aiMessage: TranscriptMessage = {
        text,
        timestamp,
        speaker: 'ai',
        id: `ai-${timestamp}`,
      };
      
      return {
        messages: [...context.messages, aiMessage],
        aiResponseInProgress: '',
        aiResponsePendingText: null,
        aiResponseStartedTimestamp: undefined,
        aiTextResponseComplete: true,
        userTranscriptComplete: false,
        // 🎯 Text-audio sync: Reset audio-received flag for the next response
        hasReceivedAudioForResponse: false,
        // Clear currentTranscriptId to prevent stale ID from being used by late EMPTY_TRANSCRIPT
        // events. Without this, markUnintelligible could remove the wrong message.
        currentTranscriptId: null,
      };
    }),
    
    clearAIResponse: assign({
      aiResponseInProgress: '',
      aiResponsePendingText: null,
      aiTextResponseComplete: false,
      aiResponseStartedTimestamp: undefined,
      // Clear currentTranscriptId to prevent stale ID issues (same reason as finalizeAIResponse)
      currentTranscriptId: null,
      // 🎯 Text-audio sync: Reset audio-received flag for the next response
      hasReceivedAudioForResponse: false,
    }),
    
    // Audio state actions
    setRecording: assign({ isRecording: (_, params: { recording: boolean }) => params.recording }),
    setPlaying: assign({ isPlaying: (_, params: { playing: boolean }) => params.playing }),
    // 🎯 Text-audio sync: Set isPlaying context when playback starts/stops.
    // setPlayingTrue also marks that audio has been received for this response —
    // this stays true through subsequent stop/start cycles (underruns) so the
    // aiFinishing guard knows audio is expected even during brief isPlaying=false
    // windows. It's reset on finalize/clear (new response).
    setPlayingTrue: assign({ isPlaying: true, hasReceivedAudioForResponse: true }),
    setPlayingFalse: assign({ isPlaying: false }),
    setAudioLevel: assign(({ event }) => {
      if (event.type !== 'AUDIO_LEVEL_CHANGED') return {};
      return { audioLevel: event.level };
    }),
    
    // Mode actions
    setInputMode: assign(({ event }) => {
      if (event.type !== 'SET_INPUT_MODE') return {};
      return { inputMode: event.mode };
    }),
    toggleAvatarAudio: assign(({ context }) => ({
      avatarAudioEnabled: !context.avatarAudioEnabled,
    })),
    setMicrophoneFallback: assign(({ event }) => {
      if (event.type !== 'SET_MICROPHONE_FALLBACK') return {};
      return { microphoneFallback: event.enabled };
    }),
    
    // Analysis mode actions
    enterAnalysisMode: assign({ analysisMode: true }),
    exitAnalysisMode: assign({ analysisMode: false, analysisModeProcessing: false }),
    setAnalysisProcessing: assign(({ event }) => {
      if (event.type !== 'SET_ANALYSIS_PROCESSING') return {};
      return { analysisModeProcessing: event.processing };
    }),
    
    // Message management actions
    clearMessages: assign({
      messages: [],
      currentTranscriptId: null,
      transcriptInProgress: '',
      aiResponseInProgress: '',
    }),
    
    addMessage: assign(({ context, event }) => {
      if (event.type !== 'ADD_MESSAGE') return {};
      return { messages: [...context.messages, event.message] };
    }),
    
    updateMessage: assign(({ context, event }) => {
      if (event.type !== 'UPDATE_MESSAGE') return {};
      const { id, updates } = event;
      return {
        messages: context.messages.map(msg =>
          msg.id === id ? { ...msg, ...updates } : msg
        ),
      };
    }),
    
    removeMessage: assign(({ context, event }) => {
      if (event.type !== 'REMOVE_MESSAGE') return {};
      return {
        messages: context.messages.filter(msg => msg.id !== event.id),
      };
    }),
  },
  
  guards: {
    canRetry: ({ context }) => context.retryCount < 5,
    // 🎯 Text-audio sync: Guard for aiFinishing transition — delay finalization
    // if audio has been received for this response, EVEN IF not currently playing.
    // This is critical: isPlaying is false during prebuffering (first ~800ms before
    // enough audio is buffered) and during brief buffer underruns. If AI_RESPONSE_DONE
    // arrives during those windows, finalizing immediately would show the full text
    // while audio is still playing (the "sem itálico" bug). hasReceivedAudioForResponse
    // is set on the first PLAYBACK_STARTED and only cleared on new response / cancel,
    // so it stays true through prebuffering gaps and underruns.
    isAudioPlaying: ({ context }) => context.isPlaying || context.hasReceivedAudioForResponse,
  },
}).createMachine({
  id: 'conversation',
  type: 'parallel',
  context: initialContext,
  
  states: {
    // ========================================================================
    // Connection State Machine
    // ========================================================================
    connection: {
      initial: 'idle',
      states: {
        idle: {
          on: {
            CONNECT: {
              target: 'connecting',
              actions: ['clearError', 'resetRetryCount'],
            },
          },
        },
        
        connecting: {
          on: {
            CONNECTED: {
              target: 'ready',
              actions: 'resetRetryCount',
            },
            CONNECTION_ERROR: {
              target: 'error',
              actions: [
                {
                  type: 'setError',
                  params: ({ event }) => ({ error: event.error }),
                },
              ],
            },
            DISCONNECT: 'idle',
          },
        },
        
        ready: {
          on: {
            DISCONNECT: 'idle',
            CONNECTION_ERROR: {
              target: 'error',
              actions: [
                {
                  type: 'setError',
                  params: ({ event }) => ({ error: event.error }),
                },
              ],
            },
          },
        },
        
        error: {
          on: {
            RETRY: {
              target: 'reconnecting',
              guard: 'canRetry',
              actions: 'incrementRetryCount',
            },
            DISCONNECT: 'idle',
            CONNECT: {
              target: 'connecting',
              actions: ['clearError', 'resetRetryCount'],
            },
          },
        },
        
        reconnecting: {
          on: {
            CONNECTED: {
              target: 'ready',
              actions: 'resetRetryCount',
            },
            CONNECTION_ERROR: {
              target: 'error',
              actions: [
                {
                  type: 'setError',
                  params: ({ event }) => ({ error: event.error }),
                },
              ],
            },
            DISCONNECT: 'idle',
          },
        },
      },
    },
    
    // ========================================================================
    // Conversation State Machine
    // 
    // REFACTORED (2026-01): Visual state vs data state separation
    // 
    // VAD_SPEECH_DETECTED: Visual feedback only (waveform shows immediately)
    // SERVER_SPEECH_STARTED: Message created with server's item_id
    // TRANSCRIPT_RECEIVED: Direct lookup by item_id (no correlation!)
    // ========================================================================
    conversation: {
      initial: 'waiting',
      states: {
        waiting: {
          description: 'Ready for user or AI to speak',
          on: {
            // Mode switch: clear any residual user-speech visual state
            RESET_USER_SPEECH: {
              actions: 'resetUserSpeech',
            },
            // Early visual hint — waveform appears before gate opens
            VAD_SPEECH_LIKELY: {
              actions: 'setUserActivelySpeaking',
            },
            // VAD detected speech - visual feedback only, NO message created
            VAD_SPEECH_DETECTED: {
              target: 'userSpeaking',
              actions: 'setUserActivelySpeaking',
            },
            // Server may detect speech before client VAD (race condition)
            // Handle it by creating message and transitioning to userSpeaking
            SERVER_SPEECH_STARTED: {
              target: 'userSpeaking',
              actions: ['setUserActivelySpeaking', 'createPlaceholder'],
            },
            // PTT: User pressed the push-to-talk button
            // In PTT mode, there's no VAD or SERVER_SPEECH_STARTED - user controls start/stop
            PTT_PRESSED: {
              target: 'userSpeaking',
              actions: ['setUserActivelySpeaking', 'createPTTPlaceholder'],
            },
            AI_RESPONSE_DELTA: {
              target: 'aiResponding',
              actions: 'appendAIResponseDelta',
            },
            // Handle late transcript arrival (e.g., AI_RESPONSE_DONE arrived before transcript)
            // This can happen when user interrupts AI and AI finishes before transcript arrives
            TRANSCRIPT_RECEIVED: {
              actions: 'fillTranscript',
            },
            EMPTY_TRANSCRIPT: {
              actions: 'markUnintelligible',
            },
            // Safety timeout fired after transitioning here from aiResponding
            // This happens when user makes noise during AI response, AI finishes,
            // and then the safety timeout fires (2s later). Reset visual state.
            NOISE_DETECTED: {
              actions: 'resetVisualState',
            },
          },
        },
        
        userSpeaking: {
          description: 'User is currently speaking (waveform visible, no message yet)',
          on: {
            // Mode switch: unstick from phantom speech detection (e.g. server
            // VAD fired on degraded audio in keyboard mode but never sent
            // speech_stopped). Without this, PTT_PRESSED is dropped here.
            RESET_USER_SPEECH: {
              target: 'waiting',
              actions: 'resetUserSpeech',
            },
            // Server confirmed speech with item_id - NOW create the message
            SERVER_SPEECH_STARTED: {
              actions: 'createPlaceholder',
            },
            SPEECH_STOPPED: {
              target: 'waitingForTranscript',
              actions: 'setUserSpeechStopped',
            },
            // PTT: User released the push-to-talk button
            // Transitions to waitingForTranscript (commit happens in the hook)
            PTT_RELEASED: {
              target: 'waitingForTranscript',
              actions: 'setUserSpeechStopped',
            },
            // PTT: User released PTT but no speech detected (silence/ambient noise).
            // Remove the placeholder and go back to waiting without committing audio.
            PTT_SILENCE_DETECTED: {
              target: 'waiting',
              actions: ['removePlaceholder', 'resetVisualState'],
            },
            // Noise detected before server confirmed - reset visual state and speaking flag
            NOISE_DETECTED: {
              target: 'waiting',
              actions: 'resetVisualState',
            },
            // Handle transcript in progress
            TRANSCRIPT_IN_PROGRESS: {
              actions: 'updateTranscriptInProgress',
            },
            // Late transcript from a previous speech segment arriving while user is speaking again
            TRANSCRIPT_RECEIVED: {
              actions: 'fillTranscript',
            },
            // Same rationale - empty transcript from previous segment
            EMPTY_TRANSCRIPT: {
              actions: 'markUnintelligible',
            },
            // AI starts responding while user is speaking - enter interruption flow
            AI_RESPONSE_DELTA: {
              target: 'aiResponding',
              actions: 'appendAIResponseDelta',
            },
            // Quick AI response completes while user is still in speaking state
            AI_RESPONSE_DONE: {
              target: 'waiting',
              actions: 'finalizeAIResponse',
            },
          },
        },
        
        waitingForTranscript: {
          description: 'User stopped speaking, waiting for transcript (loading visible)',
          on: {
            // Mode switch: unstick from phantom speech detection
            RESET_USER_SPEECH: {
              target: 'waiting',
              actions: 'resetUserSpeech',
            },
            // Server may send speech_started late - handle it
            SERVER_SPEECH_STARTED: {
              actions: 'createPlaceholder',
            },
            TRANSCRIPT_RECEIVED: {
              target: 'aiResponding',
              actions: 'fillTranscript',
            },
            EMPTY_TRANSCRIPT: {
              target: 'waiting',
              actions: 'markUnintelligible',
            },
            // User started speaking again before transcript arrived
            VAD_SPEECH_DETECTED: {
              target: 'userSpeaking',
              actions: 'setUserActivelySpeaking',
            },
            // PTT: User pressed push-to-talk again before transcript arrived
            PTT_PRESSED: {
              target: 'userSpeaking',
              actions: ['setUserActivelySpeaking', 'createPTTPlaceholder'],
            },
            // Safety timeout fired - treat as noise and reset visual state
            // This can happen if the safety timeout fires while waiting for transcript
            NOISE_DETECTED: {
              actions: 'resetVisualState',
            },
            // Server can process and start responding before transcript text arrives at client
            AI_RESPONSE_DELTA: {
              target: 'aiResponding',
              actions: 'appendAIResponseDelta',
            },
            // Quick AI response completes before transcript text arrives
            AI_RESPONSE_DONE: {
              target: 'waiting',
              actions: 'finalizeAIResponse',
            },
            // Late SPEECH_STOPPED arriving after VAD already transitioned here
            SPEECH_STOPPED: {
              actions: 'setUserSpeechStopped',
            },
          },
        },
        
        aiResponding: {
          description: 'AI is responding (text streaming + audio playing)',
          on: {
            // Mode switch: clear stuck user-speech visual state without
            // disrupting the ongoing AI response.
            RESET_USER_SPEECH: {
              actions: 'resetUserSpeech',
            },
            AI_RESPONSE_DELTA: {
              actions: 'appendAIResponseDelta',
            },
            // 🎯 Text-audio sync: If audio is still playing, hold text in pending state
            // until PLAYBACK_STOPPED. If no audio playing, finalize immediately (legacy behavior).
            AI_RESPONSE_DONE: [
              {
                target: 'aiFinishing',
                guard: 'isAudioPlaying',
                actions: 'storeAIResponsePending',
              },
              {
                target: 'waiting',
                actions: 'finalizeAIResponse',
              },
            ],
            AI_AUDIO_DONE: {
              // Audio finished but text might still be streaming
              // Only transition when both are done
            },
            // Early visual hint during AI response — show waveform immediately
            VAD_SPEECH_LIKELY: {
              actions: 'setUserActivelySpeaking',
            },
            // User can speak while AI is responding (interruption)
            // - Set active speaking flag for waveform display
            // - Stay in aiResponding (AI text continues to stream)
            // - Both bubbles visible in parallel
            // - AI bubble ALWAYS completes, NEVER disappears
            VAD_SPEECH_DETECTED: {
              // No target = stay in aiResponding state
              actions: 'setUserActivelySpeaking',
            },
            // PTT: User pressed push-to-talk during AI response (interruption)
            PTT_PRESSED: {
              // Stay in aiResponding - AI continues, but show user waveform
              actions: ['setUserActivelySpeaking', 'createPTTPlaceholder'],
            },
            // PTT: User released push-to-talk during AI response
            PTT_RELEASED: {
              actions: 'setUserSpeechStopped',
            },
            // Server confirmed user speech - create their message
            SERVER_SPEECH_STARTED: {
              // No target = stay in aiResponding state
              actions: 'createPlaceholder',
            },
            // Handle user speech lifecycle while AI continues responding.
            // 🔧 FIX: Use fillTranscript instead of clearTranscriptId.
            // In PTT mode, the placeholder has a local ptt-* ID but the transcript arrives
            // with the server's item_id. UPDATE_MESSAGE (sent by useTranscriptManager) fails
            // silently because the IDs don't match. fillTranscript has fallback logic that
            // finds the pending placeholder by looking for empty user messages, fixing the
            // "stuck loading bubble" bug where user messages never appeared after 2-3 exchanges.
            TRANSCRIPT_RECEIVED: {
              actions: 'fillTranscript',
            },
            EMPTY_TRANSCRIPT: {
              actions: 'clearTranscriptId',
            },
            SPEECH_STOPPED: {
              actions: 'setUserSpeechStopped',
            },
            NOISE_DETECTED: {
              actions: 'resetVisualState',
            },
            CANCEL_RESPONSE: {
              target: 'waiting',
              actions: 'clearAIResponse',
            },
          },
        },
        
        // 🎯 Text-audio sync: AI text is complete but audio is still playing.
        // The full text is held in aiResponsePendingText and aiResponseInProgress
        // (not cleared) so useSyncedTextReveal continues throttling the reveal.
        // When audio stops (PLAYBACK_STOPPED), we finalize and add the message.
        // Safety timeout (AI_FINISHING_TIMEOUT) prevents getting stuck if
        // PLAYBACK_STOPPED never fires.
        aiFinishing: {
          description: 'AI text complete, waiting for audio playback to finish',
          on: {
            // Mode switch: clear stuck user-speech visual state without
            // disrupting the ongoing AI finalization.
            RESET_USER_SPEECH: {
              actions: 'resetUserSpeech',
            },
            // Audio finished playing — NOW finalize and add message to transcript
            PLAYBACK_STOPPED: {
              target: 'waiting',
              actions: 'finalizeAIResponse',
            },
            // Safety timeout — finalize even if PLAYBACK_STOPPED didn't fire
            AI_FINISHING_TIMEOUT: {
              target: 'waiting',
              actions: 'finalizeAIResponse',
            },
            // User interrupted — finalize immediately (don't hold text)
            CANCEL_RESPONSE: {
              target: 'waiting',
              actions: 'finalizeAIResponse',
            },
            // Late text delta (shouldn't happen, but safe)
            AI_RESPONSE_DELTA: {
              actions: 'appendAIResponseDelta',
            },
            // User can still speak/interrupt during finishing
            VAD_SPEECH_LIKELY: {
              actions: 'setUserActivelySpeaking',
            },
            VAD_SPEECH_DETECTED: {
              actions: 'setUserActivelySpeaking',
            },
            PTT_PRESSED: {
              actions: ['setUserActivelySpeaking', 'createPTTPlaceholder'],
            },
            PTT_RELEASED: {
              actions: 'setUserSpeechStopped',
            },
            SERVER_SPEECH_STARTED: {
              actions: 'createPlaceholder',
            },
            TRANSCRIPT_RECEIVED: {
              actions: 'fillTranscript',
            },
            EMPTY_TRANSCRIPT: {
              actions: 'clearTranscriptId',
            },
            SPEECH_STOPPED: {
              actions: 'setUserSpeechStopped',
            },
            NOISE_DETECTED: {
              actions: 'resetVisualState',
            },
          },
        },
      },
    },
    
    // ========================================================================
    // Audio State (parallel, independent of conversation)
    // ========================================================================
    audio: {
      type: 'parallel',
      states: {
        recording: {
          initial: 'inactive',
          states: {
            inactive: {
              on: {
                RECORDING_STARTED: 'active',
              },
            },
            active: {
              on: {
                RECORDING_STOPPED: 'inactive',
              },
            },
          },
        },
        playback: {
          initial: 'inactive',
          states: {
            inactive: {
              on: {
                PLAYBACK_STARTED: {
                  target: 'active',
                  actions: 'setPlayingTrue',
                },
              },
            },
            active: {
              on: {
                PLAYBACK_STOPPED: {
                  target: 'inactive',
                  actions: 'setPlayingFalse',
                },
              },
            },
          },
        },
      },
    },
    
    // ========================================================================
    // Mode State (parallel, tracks input/analysis modes)
    // ========================================================================
    mode: {
      type: 'parallel',
      states: {
        input: {
          initial: 'ptt',
          states: {
            ptt: {
              on: {
                SET_INPUT_MODE: [
                  {
                    target: 'keyboard',
                    guard: ({ event }) => event.mode === 'keyboard',
                    actions: 'setInputMode',
                  },
                  {
                    target: 'voice',
                    guard: ({ event }) => event.mode === 'voice',
                    actions: 'setInputMode',
                  },
                ],
              },
            },
            voice: {
              on: {
                SET_INPUT_MODE: [
                  {
                    target: 'keyboard',
                    guard: ({ event }) => event.mode === 'keyboard',
                    actions: 'setInputMode',
                  },
                  {
                    target: 'ptt',
                    guard: ({ event }) => event.mode === 'ptt',
                    actions: 'setInputMode',
                  },
                ],
              },
            },
            keyboard: {
              on: {
                SET_INPUT_MODE: [
                  {
                    target: 'voice',
                    guard: ({ event }) => event.mode === 'voice',
                    actions: 'setInputMode',
                  },
                  {
                    target: 'ptt',
                    guard: ({ event }) => event.mode === 'ptt',
                    actions: 'setInputMode',
                  },
                ],
              },
            },
          },
        },
        analysis: {
          initial: 'normal',
          states: {
            normal: {
              on: {
                ENTER_ANALYSIS_MODE: {
                  target: 'analysisMode',
                  actions: 'enterAnalysisMode',
                },
              },
            },
            analysisMode: {
              on: {
                EXIT_ANALYSIS_MODE: {
                  target: 'normal',
                  actions: 'exitAnalysisMode',
                },
                SET_ANALYSIS_PROCESSING: {
                  actions: 'setAnalysisProcessing',
                },
              },
            },
          },
        },
      },
    },
  },
  
  // Global events that can be handled from any state
  // 
  // IMPORTANT: In XState v5, state-level handlers take precedence over root-level handlers.
  // The data event fallbacks below only fire when no state-specific handler exists.
  // They are pure safety nets to prevent events from being silently dropped.
  on: {
    CLEAR_ERROR: {
      actions: 'clearError',
    },
    CLEAR_MESSAGES: {
      actions: 'clearMessages',
    },
    ADD_MESSAGE: {
      actions: 'addMessage',
    },
    UPDATE_MESSAGE: {
      actions: 'updateMessage',
    },
    REMOVE_MESSAGE: {
      actions: 'removeMessage',
    },
    AUDIO_LEVEL_CHANGED: {
      actions: 'setAudioLevel',
    },
    TOGGLE_AVATAR_AUDIO: {
      actions: 'toggleAvatarAudio',
    },
    SET_MICROPHONE_FALLBACK: {
      actions: 'setMicrophoneFallback',
    },
    // === Global fallback handlers for critical data events ===
    // These ensure events are NEVER silently dropped, even in unexpected states.
    TRANSCRIPT_RECEIVED: {
      actions: 'fillTranscript',
    },
    EMPTY_TRANSCRIPT: {
      actions: 'markUnintelligible',
    },
    AI_RESPONSE_DELTA: {
      actions: 'appendAIResponseDelta',
    },
    AI_RESPONSE_DONE: {
      actions: 'finalizeAIResponse',
    },
    SERVER_SPEECH_STARTED: {
      actions: 'createPlaceholder',
    },
    VAD_SPEECH_LIKELY: {
      actions: 'setUserActivelySpeaking',
    },
    VAD_SPEECH_DETECTED: {
      actions: 'setUserActivelySpeaking',
    },
    SPEECH_STOPPED: {
      actions: 'setUserSpeechStopped',
    },
    NOISE_DETECTED: {
      actions: 'resetVisualState',
    },
    // PTT global fallbacks
    PTT_PRESSED: {
      actions: ['setUserActivelySpeaking', 'createPTTPlaceholder'],
    },
    PTT_RELEASED: {
      actions: 'setUserSpeechStopped',
    },
    PTT_SILENCE_DETECTED: {
      actions: ['removePlaceholder', 'resetVisualState'],
    },
  },
});

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get connection status string from machine state
 */
export function getConnectionStatus(state: any): 'disconnected' | 'connecting' | 'connected' | 'error' | 'reconnecting' {
  if (state.matches({ connection: 'idle' })) return 'disconnected';
  if (state.matches({ connection: 'connecting' })) return 'connecting';
  if (state.matches({ connection: 'ready' })) return 'connected';
  if (state.matches({ connection: 'reconnecting' })) return 'reconnecting';
  if (state.matches({ connection: 'error' })) return 'error';
  return 'disconnected';
}

/**
 * Check if user is currently speaking
 * Returns true if in userSpeaking state OR if actively speaking during interruption
 */
export function isUserSpeaking(state: any): boolean {
  return state.matches({ conversation: 'userSpeaking' }) || state.context.isUserActivelySpeaking;
}

/**
 * Check if AI is currently responding
 */
export function isAISpeaking(state: any): boolean {
  return state.matches({ conversation: 'aiResponding' });
}

/**
 * Check if waiting for transcript
 */
export function isWaitingForTranscript(state: any): boolean {
  return state.matches({ conversation: 'waitingForTranscript' });
}

/**
 * Check if connected and ready
 */
export function isConnected(state: any): boolean {
  return state.matches({ connection: 'ready' });
}

/**
 * Check if recording is active
 */
export function isRecordingActive(state: any): boolean {
  return state.matches({ audio: { recording: 'active' } });
}

/**
 * Check if playback is active
 */
export function isPlaybackActive(state: any): boolean {
  return state.matches({ audio: { playback: 'active' } });
}

export type ConversationMachine = typeof conversationMachine;
